import { memo, useCallback, useState } from 'react';
import { Box, Icon, Spinner, Table } from '@amzn/awsui-components-core';
import { PublicationSearchResultItem } from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';

import PublicationLink from '../publication/PublicationLink';

const DEFAULT_VISIBLE_COUNT = 5;
const BATCH_SIZE = 10;

interface DuplicateCheckResultViewProps {
  readonly isSearching: boolean;
  readonly duplicates: readonly PublicationSearchResultItem[];
  /** True when the duplicate check failed — show a "couldn't verify" note, not "none found". */
  readonly isCheckFailed?: boolean;
}

/** Displays duplicate content search results: a spinner while searching, a success indicator when none found, or a warning table listing matches. */
function DuplicateCheckResultView({
  isSearching,
  duplicates,
  isCheckFailed = false,
}: DuplicateCheckResultViewProps) {
  const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE_COUNT);

  const handleShowMore = useCallback(() => {
    setVisibleCount((prev) => prev + BATCH_SIZE);
  }, []);

  if (isSearching) {
    return <SearchingIndicator />;
  }

  // A failed check must not read as "no duplicates" — surface a low-severity note.
  if (isCheckFailed) {
    return <CheckFailedIndicator />;
  }

  if (duplicates.length === 0) {
    return <NoDuplicatesIndicator />;
  }

  return (
    <DuplicatesFound
      duplicates={duplicates}
      visibleCount={visibleCount}
      onShowMore={handleShowMore}
    />
  );
}

const SearchingIndicator = memo(function SearchingIndicator() {
  return (
    <Box variant="div">
      <Spinner />
      <Box
        variant="p"
        display="inline"
        fontSize="body-s"
        color="text-body-secondary"
        margin={{ left: 'xs' }}
      >
        Searching for duplicate content...
      </Box>
    </Box>
  );
});

const CheckFailedIndicator = memo(function CheckFailedIndicator() {
  return (
    <Box variant="div">
      <Icon variant="subtle" name="status-warning" />
      <Box
        variant="p"
        display="inline"
        fontSize="body-s"
        color="text-body-secondary"
        margin={{ left: 'xs' }}
      >
        Couldn&apos;t verify whether the title is a duplicate. Try again shortly.
      </Box>
    </Box>
  );
});

const NoDuplicatesIndicator = memo(function NoDuplicatesIndicator() {
  return (
    <Box variant="div">
      <Icon variant="success" name="status-positive" />
      <Box
        variant="p"
        display="inline"
        fontSize="body-s"
        color="text-status-success"
        margin={{ left: 'xs' }}
      >
        No similar titles found
      </Box>
    </Box>
  );
});

interface DuplicatesFoundProps {
  readonly duplicates: readonly PublicationSearchResultItem[];
  readonly visibleCount: number;
  readonly onShowMore: () => void;
}

const COLUMN_DEFINITIONS = [
  {
    id: 'title',
    header: 'Title',
    cell: (item: PublicationSearchResultItem) => (
      <PublicationLink
        contentName={item.contentName}
        contentId={item.contentId}
        workflowId={item.publicationWorkflowId}
        target="_blank"
      />
    ),
  },
  {
    id: 'contentType',
    header: 'Content type',
    cell: (item: PublicationSearchResultItem) => (
      <Box variant="p" color="text-body-secondary">
        {item.contentType || '-'}
      </Box>
    ),
  },
  {
    id: 'lastUpdate',
    header: 'Last update',
    cell: (item: PublicationSearchResultItem) => {
      const dt = DateTime.fromISO(item.publishDate);
      return (
        <Box variant="p" color="text-body-secondary">
          {dt.isValid ? dt.toLocaleString(DateTime.DATE_FULL) : '-'}
        </Box>
      );
    },
  },
];

const DuplicatesFound = memo(function DuplicatesFound({
  duplicates,
  visibleCount,
  onShowMore,
}: DuplicatesFoundProps) {
  const totalCount = duplicates.length;
  const visibleItems = duplicates.slice(0, visibleCount);
  const remainingCount = Math.min(BATCH_SIZE, totalCount - visibleCount);

  return (
    <div>
      <Box color="text-body-secondary">
        <Icon variant="error" name="status-warning" />
        <Box variant="p" display="inline" fontSize="body-s" margin={{ left: 'xs' }}>
          <Box variant="strong" color="text-body-secondary">
            We found {totalCount} article{totalCount > 1 ? 's' : ''} that{' '}
            {totalCount > 1 ? 'are' : 'is'} similar to your title
          </Box>
        </Box>
        <Box variant="p" fontSize="body-s" color="text-body-secondary" margin={{ left: 's' }}>
          To prevent duplicate content please review the articles below to ensure you're not
          creating duplicate content.
        </Box>
      </Box>
      <Table
        loadingText="searching duplicate content"
        columnDefinitions={COLUMN_DEFINITIONS}
        items={visibleItems as PublicationSearchResultItem[]}
      />
      {visibleCount < totalCount && (
        <Box variant="div" margin={{ top: 'xs' }}>
          <button
            type="button"
            onClick={onShowMore}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <Icon variant="success" name="treeview-expand" />
            <Box
              variant="p"
              display="inline"
              fontSize="body-s"
              color="text-body-secondary"
              margin={{ left: 'xs' }}
            >
              Show {remainingCount} more
            </Box>
          </button>
        </Box>
      )}
    </div>
  );
});

export default memo(DuplicateCheckResultView);
